import React, { createContext, useCallback, useContext, useState } from "react";
import { createPortal } from "react-dom";
import { Check, CircleAlert, X } from "lucide-react";

const ToastContext = createContext(null);

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error("useToast must be used within ToastProvider");
  }
  return ctx;
}

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const dismiss = useCallback((id) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const show = useCallback(
    (message, type = "success") => {
      const id = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
      setToasts((current) => [...current, { id, message, type }]);
      window.setTimeout(() => dismiss(id), 4500);
      return id;
    },
    [dismiss],
  );

  const toast = {
    show,
    success: (message) => show(message, "success"),
    error: (message) => show(message, "error"),
  };

  return (
    <ToastContext.Provider value={toast}>
      {children}
      {createPortal(
        <div
          className="site_toaster"
          aria-live="polite"
          aria-relevant="additions"
        >
          {toasts.map((item) => {
            const isSuccess = item.type === "success";
            return (
              <div
                key={item.id}
                role="status"
                className={`site_toast ${isSuccess ? "is-success" : "is-error"}`}
              >
                <span className="site_toast_icon">
                  {isSuccess ? (
                    <Check size={16} strokeWidth={2.5} />
                  ) : (
                    <CircleAlert size={16} />
                  )}
                </span>
                <p className="site_toast_message">{item.message}</p>
                <button
                  type="button"
                  onClick={() => dismiss(item.id)}
                  className="site_toast_close"
                  aria-label="Dismiss notification"
                >
                  <X size={16} />
                </button>
              </div>
            );
          })}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  );
}
